import type { EChartsTheme, FormatAmount, PrivacyMask } from '../types';

// Fixed so callers can measure label text with the same font the axis actually renders.
export const AXIS_LABEL_FONT_SIZE = 12;
export type AxisLabelBounds = {
  center: number;
  width: number;
};

// The bounds are measured after ECharts has laid out the axis, so rotation is triggered
// by real label collisions rather than a data-count or viewport-width heuristic.
export const hasOverlappingAxisLabels = (labels: AxisLabelBounds[]): boolean => {
  const sortedLabels = [...labels].sort((a, b) => a.center - b.center);

  return sortedLabels.some((label, index) => {
    const previous = sortedLabels[index - 1];
    return previous !== undefined && label.center - label.width / 2 < previous.center + previous.width / 2;
  });
};

type AxisLabelConfig = {
  formatAmount: FormatAmount;
  privacyMask: PrivacyMask;
  useNumberPrefix?: boolean;
  // Largest absolute currency value on the axis. Sub-dollar ranges need fractional labels;
  // otherwise distinct ECharts ticks such as -0.4 and -0.2 both render as "-$0".
  currencyMaxMagnitude?: number;
  // Percent value axis: append '%' and abbreviate large magnitudes as M/B/T, falling back to
  // scientific notation above 1e15. Matches the amcharts4 IRR chart's number formatter, which
  // picked the format bucket from the largest magnitude on the axis (see `percentMaxMagnitude`).
  percent?: boolean;
  // Largest absolute value on the axis; selects the percent format bucket so every tick shares
  // one notation (all abbreviated, all scientific, or all plain) as amcharts4 did.
  percentMaxMagnitude?: number;
};

// SIMPL-34719: decimals for the plain bucket, derived from the axis's own scale so ticks stay
// distinguishable. Roughly two significant digits at `maxMagnitude` (0.15 -> 2, 0.03 -> 3, 5 -> 1,
// 50 -> 0); paired with `maximumFractionDigits` below, integer ticks still render as '40%'. Rounding
// to whole percents (as amcharts4 did) collapsed near-zero TWR axes to repeated '0%' labels.
const scaleDecimals = (maxMagnitude: number): number => {
  if (!Number.isFinite(maxMagnitude) || maxMagnitude <= 0) return 0;
  return Math.min(6, Math.max(0, 1 - Math.floor(Math.log10(maxMagnitude))));
};

// amcharts4 buckets: `#,###'%'` (plain), `#.0a'%'` (M/B/T, one decimal), `#.##e'%'` (scientific).
const formatPercentLabel = (val: number, maxMagnitude: number): string => {
  if (maxMagnitude >= 1e15) return `${val.toExponential(2)}%`;
  if (maxMagnitude >= 1e6) {
    const abs = Math.abs(val);
    const sign = val < 0 ? '-' : '';
    if (abs >= 1e12) return `${sign}${(abs / 1e12).toFixed(1)}T%`;
    if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(1)}B%`;
    if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(1)}M%`;
    return `${sign}${abs.toFixed(1)}%`;
  }
  const decimals = scaleDecimals(maxMagnitude);
  // ECharts tick values carry float noise (e.g. -1.39e-17); rounding first keeps those from
  // rendering as '-0%'. `|| 0` normalizes the -0 that toFixed produces for small negatives.
  const rounded = Number(val.toFixed(decimals)) || 0;
  return `${rounded.toLocaleString('en-US', { maximumFractionDigits: decimals })}%`;
};

export const buildAxisLabelFormatter =
  (config: AxisLabelConfig) =>
  (val: number): string => {
    const {
      privacyMask,
      formatAmount,
      useNumberPrefix = false,
      currencyMaxMagnitude = 0,
      percent = false,
      percentMaxMagnitude = 0,
    } = config;
    if (percent) return formatPercentLabel(val, percentMaxMagnitude);
    if (useNumberPrefix) {
      const abs = Math.abs(val);
      // Keep the sign ahead of the currency symbol (-$2K, not $-2K).
      const sign = val < 0 ? '-' : '';
      // SIMPL-28994 shortened-notation rules (mirrors the AMCharts BigNumberCurrencyFormatter):
      //   >= $1M  -> 2 decimals ('$1.42M') so adjacent axis rows that differ by < 100K stay
      //             distinct instead of both reading '$1.4M'. Auto-scales to B/T.
      //   >= $1K  -> up to 1 decimal ('$2.5K', '$950K'); trailing '.0' is dropped.
      //   < $1K   -> full formatting via formatAmount.
      if (abs >= 1_000_000_000_000) {
        return String(privacyMask(`${sign}$${(abs / 1_000_000_000_000).toFixed(2)}T`));
      }
      if (abs >= 1_000_000_000) {
        return String(privacyMask(`${sign}$${(abs / 1_000_000_000).toFixed(2)}B`));
      }
      if (abs >= 1_000_000) {
        return String(privacyMask(`${sign}$${(abs / 1_000_000).toFixed(2)}M`));
      }
      if (abs >= 1_000) {
        const n = abs / 1_000;
        return String(privacyMask(`${sign}$${Number.isInteger(n) ? n.toFixed(0) : n.toFixed(1)}K`));
      }
    }
    const currencyDecimals =
      currencyMaxMagnitude > 0 && currencyMaxMagnitude < 1 ? scaleDecimals(currencyMaxMagnitude) : 0;
    const rounded = currencyDecimals > 0 ? Number(val.toFixed(currencyDecimals)) || 0 : val;
    const format = currencyDecimals > 0 ? `0,0.${'0'.repeat(currencyDecimals)}` : '0,0';
    return String(privacyMask(formatAmount(rounded, 'USD', format)));
  };

const buildAxisTitle = (title: string | undefined, theme: EChartsTheme, rotated: boolean, nameGap: number) =>
  title
    ? {
        name: title,
        nameLocation: 'middle' as const,
        nameGap,
        ...(rotated ? { nameRotate: 90 } : {}),
        // Matches the chart-title textStyle below so axis titles share the same typography.
        nameTextStyle: { color: theme.textColor, fontFamily: theme.fontFamily, fontSize: 14, fontWeight: 500 },
      }
    : {};

export const buildCategoryAxis = (
  categories: string[],
  theme: EChartsTheme,
  title?: string,
  showGridLines?: boolean,
  // Optional sparse labels aligned to `categories` ('' hides a label). When provided,
  // the axis paints these instead of the raw category text — lets callers control which
  // date labels appear (see buildDateAxisLabels). `hideOverlap` stays on as a width backstop.
  labelOverrides?: string[] | null,
) => ({
  type: 'category' as const,
  data: categories,
  axisLabel: {
    color: theme.textTertiaryColor,
    fontSize: AXIS_LABEL_FONT_SIZE,
    interval: 0,
    // Let ECharts auto-hide labels that would overlap. Charts can also opt into
    // post-layout rotation; this remains the density backstop.
    hideOverlap: true,
    ...(labelOverrides ? { formatter: (_value: string, index: number) => labelOverrides[index] ?? '' } : {}),
  },
  axisLine: { lineStyle: { color: theme.dividerColor } },
  axisTick: { show: false },
  splitLine: { show: Boolean(showGridLines), lineStyle: { color: theme.gridColor } },
  ...buildAxisTitle(title, theme, false, 28),
});

type ValueAxisOptions = {
  theme: EChartsTheme;
  formatAmount: FormatAmount;
  privacyMask: PrivacyMask;
  title?: string;
  showGridLines?: boolean;
  nameGap?: number;
  useNumberPrefix?: boolean;
  currencyMaxMagnitude?: number;
  splitNumber?: number;
  splitLineColor?: string;
  // Percent value axis (see buildAxisLabelFormatter). Pass `percentMaxMagnitude` (largest
  // absolute value on the axis) so all ticks share one notation.
  percent?: boolean;
  percentMaxMagnitude?: number;
  // Explicit axis extents. When set, ECharts pins the range instead of auto-scaling to a
  // padded "nice" bound — used by the projected-balances chart to match production, which
  // fixes the axis to the exact visible data extremes (see computeVisibleYRange).
  min?: number;
  max?: number;
  // Hide the first/last axis labels. Pair with pinned min/max set to exact (non-round) data
  // extremes: the line still fills the plot area, but only the interior round steps are
  // labeled (e.g. $5K/$0/-$5K) instead of stray values like "$18.6K".
  hideEdgeLabels?: boolean;
};

export const buildValueAxis = ({
  theme,
  formatAmount,
  privacyMask,
  title,
  showGridLines,
  nameGap = 28,
  useNumberPrefix,
  currencyMaxMagnitude,
  splitNumber,
  splitLineColor,
  percent,
  percentMaxMagnitude,
  min,
  max,
  hideEdgeLabels,
}: ValueAxisOptions) => ({
  type: 'value' as const,
  ...(splitNumber !== undefined && { splitNumber }),
  ...(min !== undefined && { min }),
  ...(max !== undefined && { max }),
  axisLabel: {
    color: theme.textTertiaryColor,
    fontSize: AXIS_LABEL_FONT_SIZE,
    ...(hideEdgeLabels && { showMinLabel: false, showMaxLabel: false }),
    formatter: buildAxisLabelFormatter({
      formatAmount,
      privacyMask,
      useNumberPrefix,
      currencyMaxMagnitude,
      percent,
      percentMaxMagnitude,
    }),
  },
  splitLine: { show: Boolean(showGridLines), lineStyle: { color: splitLineColor ?? theme.gridColor } },
  ...buildAxisTitle(title, theme, true, nameGap),
});
